import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { CriterionAverageItem, ExpertReportSummaryItem, QualityKpis } from '@/entities/dashboards';
import { useCriteriaOrder } from '../model/useCriteriaCatalog';
import { EmptyState } from './parts/EmptyState';
import { criteriaExtremesByRatio, fmt } from './utils';

type Props = {
  kpis: QualityKpis | null;
  /** Средние по видимым (не скрытым) критериям; в сводке группируются по отчёту-владельцу. */
  criteriaAverages: CriterionAverageItem[];
  /** Средний балл и доля низких оценок по каждому экспертному заключению выборки. */
  reportSummaries: ExpertReportSummaryItem[];
};

/** Блок сводки по одному экспертному заключению: его показатели и крайние критерии. */
type ReportBlock = {
  reportId: number;
  reportName: string;
  /** Средний общий балл заключения; `null` — баллов нет либо показатели не пришли. */
  avgTotal: number | null;
  /** Доля низких оценок заключения, %; `null` — показатели заключения не пришли. */
  lowPct: number | null;
  /** Слабейший и сильнейший критерий заключения по доле балла от максимума шкалы. */
  weakest: CriterionAverageItem | null;
  strongest: CriterionAverageItem | null;
};

/**
 * Сводная карточка: текстовый пересказ основных метрик — средний балл и доля низких оценок по всей
 * выборке, а под ними разбивка по экспертным заключениям, где у каждого свои средний балл, доля
 * низких оценок и самый слабый с самым сильным критерием. Свои показатели у заключения нужны
 * потому, что у заключений разные наборы критериев и разные шкалы — общая пара по выборке не
 * показывает положение дел по каждому из них.
 *
 * Крайние критерии определяются по доле балла от максимума своей шкалы, а не по абсолютному
 * среднему, поэтому критерий с короткой шкалой не выглядит слабым лишь из-за малого абсолюта.
 * Показатели заключения приходят с бэкенда посчитанными по всем его оценкам, поэтому скрытие
 * критерия их не меняет (как и общие показатели выборки) — скрытие влияет только на выбор крайних
 * критериев. Порядок заключений — как в каталоге (тот же, что в графике средних баллов и в
 * настройках отображения).
 */
export const SummaryCard: FC<Props> = ({ kpis, criteriaAverages, reportSummaries }) => {
  const { t } = useTranslation('qualityDashboard');
  const { reportRank } = useCriteriaOrder();

  const reportBlocks = useMemo((): ReportBlock[] => {
    const byReport = new Map<number, CriterionAverageItem[]>();
    for (const c of criteriaAverages) {
      const list = byReport.get(c.expertReportId);
      if (list) list.push(c);
      else byReport.set(c.expertReportId, [c]);
    }

    // Заключение попадает в сводку, если по нему есть показатели выборки либо видимые критерии:
    // показатели без определимых крайних критериев показать всё равно нужно, а критерии без
    // показателей остаются на прежнем месте.
    const reportIds = new Set<number>([...reportSummaries.map((s) => s.expertReportId), ...byReport.keys()]);
    const summaryById = new Map(reportSummaries.map((s) => [s.expertReportId, s]));

    return [...reportIds]
      .map((reportId): ReportBlock => {
        const items = byReport.get(reportId) ?? [];
        const summary = summaryById.get(reportId);
        return {
          reportId,
          reportName: summary?.expertReportName || items[0]?.expertReportName || '',
          avgTotal: summary?.avgTotal ?? null,
          lowPct: summary?.lowPct ?? null,
          ...criteriaExtremesByRatio(items),
        };
      })
      .sort((a, b) => reportRank(a.reportId) - reportRank(b.reportId));
  }, [criteriaAverages, reportSummaries, reportRank]);

  if (!kpis || kpis.count === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('summary.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('summary.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 text-sm">
          {/* Общие показатели выборки — над разбивкой; заголовок отделяет их от одноимённых показателей заключений. */}
          <div className="flex flex-col gap-1">
            <h4 className="font-medium text-muted-foreground">{t('summary.overall')}</h4>
            <div>
              <strong>{t('summary.avgScore')}</strong> {fmt(kpis.avgTotal)}
            </div>
            <div>
              <strong>{t('summary.lowScores')}</strong> {fmt(kpis.lowPct, 0)}%
            </div>
          </div>
          {reportBlocks.map((block) => (
            <div key={block.reportId} className="flex flex-col gap-1">
              <h4 className="font-medium text-muted-foreground">{block.reportName}</h4>
              <div>
                <strong>{t('summary.avgScore')}</strong> {fmt(block.avgTotal)}
              </div>
              <div>
                <strong>{t('summary.lowScores')}</strong> {block.lowPct === null ? '—' : `${fmt(block.lowPct, 0)}%`}
              </div>
              <div>
                <strong>{t('summary.weakestCriterion')}</strong>{' '}
                {block.weakest ? `${block.weakest.name} (${fmt(block.weakest.value)})` : '—'}
              </div>
              <div>
                <strong>{t('summary.strongestCriterion')}</strong>{' '}
                {block.strongest ? `${block.strongest.name} (${fmt(block.strongest.value)})` : '—'}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
